/* PublicInspectionPage — standalone, no app chrome, no auth.
   Rendered at /inspection/:token outside the AuthGuard. */
import { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import {
    AlertTriangle, Camera, Check, Download, Loader2, Mail, MapPin, Phone,
    ShieldAlert, X,
} from "lucide-react";
import { PublicInspectionAPI } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { cn } from "@/lib/utils";

const SEV_BAR = {
    green:  "bg-emerald-500",
    yellow: "bg-amber-500",
    red:    "bg-rose-600",
    na:     "bg-zinc-400",
};

const SEV_CHIP = {
    green:  "bg-emerald-100 text-emerald-800",
    yellow: "bg-amber-100 text-amber-800",
    red:    "bg-rose-100 text-rose-800",
    na:     "bg-zinc-100 text-zinc-700",
};

function rupees(n) {
    if (n == null) return "—";
    return "₹" + Number(n).toLocaleString("en-IN", { maximumFractionDigits: 0 });
}

function FindingCard({ item, decisions, setDecision, onPhotoClick }) {
    const sev = item.severity;
    const isDecisionable = (sev === "yellow" || sev === "red") && !item.customer_decision;
    const choice = decisions[item.finding_id] || item.customer_decision || null;
    return (
        <div
            data-testid={`pub-finding-${item.finding_id}`}
            data-severity={sev}
            className={cn(
                "relative overflow-hidden rounded-lg border bg-white p-3 shadow-sm sm:p-4",
                "dark:border-zinc-800 dark:bg-zinc-900",
            )}
        >
            <div className={cn("absolute inset-y-0 left-0 w-1.5", SEV_BAR[sev] || "bg-zinc-300")} />
            <div className="pl-3">
                <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2">
                            <h3 className="font-semibold leading-tight">{item.item_label}</h3>
                            <span className={cn(
                                "rounded-full px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider",
                                SEV_CHIP[sev] || SEV_CHIP.na,
                            )}>
                                {sev === "red" ? "Urgent"
                                    : sev === "yellow" ? "Advise"
                                    : sev === "green" ? "OK"
                                    : "N/A"}
                            </span>
                        </div>
                        {item.technician_notes ? (
                            <p className="mt-1 text-sm text-zinc-600 dark:text-zinc-300">
                                {item.technician_notes}
                            </p>
                        ) : null}
                        {item.recommended_action ? (
                            <p className="mt-1 text-sm text-zinc-800 dark:text-zinc-100">
                                <span className="font-semibold">Recommended:</span>{" "}
                                {item.recommended_action}
                            </p>
                        ) : null}
                    </div>
                    {item.estimated_cost_inr != null ? (
                        <div className="text-right">
                            <div className="font-mono text-[10px] uppercase text-zinc-500">Est.</div>
                            <div className="font-display text-lg font-bold">{rupees(item.estimated_cost_inr)}</div>
                        </div>
                    ) : null}
                </div>

                {(item.photo_signed_urls || []).length > 0 ? (
                    <div className="mt-2 flex gap-2 overflow-x-auto">
                        {item.photo_signed_urls.map((url, idx) => (
                            <button
                                key={url} type="button"
                                onClick={() => onPhotoClick(url)}
                                className="relative h-16 w-16 flex-shrink-0 overflow-hidden rounded-md border border-zinc-200"
                                data-testid={`pub-finding-${item.finding_id}-photo-${idx}`}
                            >
                                <img src={url} alt="" className="h-full w-full object-cover" />
                            </button>
                        ))}
                    </div>
                ) : null}

                {(sev === "yellow" || sev === "red") ? (
                    <div className="mt-3 flex gap-2">
                        <Button
                            type="button"
                            size="sm"
                            variant={choice === "approved" ? "default" : "outline"}
                            disabled={!isDecisionable && choice !== "approved"}
                            onClick={() => setDecision(item.finding_id, "approved")}
                            data-testid={`pub-finding-${item.finding_id}-approve`}
                            className={cn(
                                "flex-1",
                                choice === "approved" && "bg-emerald-600 hover:bg-emerald-700",
                            )}
                        >
                            <Check className="mr-1.5 h-4 w-4" />
                            Approve
                        </Button>
                        <Button
                            type="button"
                            size="sm"
                            variant={choice === "rejected" ? "default" : "outline"}
                            disabled={!isDecisionable && choice !== "rejected"}
                            onClick={() => setDecision(item.finding_id, "rejected")}
                            data-testid={`pub-finding-${item.finding_id}-reject`}
                            className={cn(
                                "flex-1",
                                choice === "rejected" && "bg-rose-600 hover:bg-rose-700",
                            )}
                        >
                            <X className="mr-1.5 h-4 w-4" />
                            Reject
                        </Button>
                    </div>
                ) : null}
            </div>
        </div>
    );
}

function ErrorScreen({ icon: Icon, title, message }) {
    return (
        <div className="flex min-h-screen items-center justify-center p-6 bg-zinc-50 dark:bg-zinc-950">
            <div className="max-w-sm text-center" data-testid="pub-error-screen">
                <Icon className="mx-auto h-10 w-10 text-zinc-400" />
                <h1 className="mt-3 text-2xl font-bold">{title}</h1>
                <p className="mt-2 text-zinc-600 dark:text-zinc-300">{message}</p>
                <div className="mt-6 font-mono text-[10px] uppercase tracking-widest text-zinc-400">
                    Powered by AutoBrix
                </div>
            </div>
        </div>
    );
}

function ThankYouScreen({ result, token }) {
    return (
        <div className="min-h-screen bg-zinc-50 px-4 py-8 dark:bg-zinc-950" data-testid="pub-thankyou">
            <div className="mx-auto max-w-md text-center">
                <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-emerald-100 text-emerald-700">
                    <Check className="h-7 w-7" />
                </div>
                <h1 className="mt-3 text-3xl font-bold">Thank you!</h1>
                <p className="mt-2 text-zinc-600 dark:text-zinc-300">
                    {result.message}
                </p>
                <div className="mt-6 rounded-lg border border-zinc-200 bg-white p-4 text-left dark:border-zinc-800 dark:bg-zinc-900">
                    <div className="flex justify-between">
                        <span className="text-zinc-500">Approved items</span>
                        <span className="font-semibold" data-testid="pub-thankyou-approved-count">
                            {result.approved_count}
                        </span>
                    </div>
                    <div className="mt-2 flex justify-between">
                        <span className="text-zinc-500">Rejected items</span>
                        <span className="font-semibold">{result.rejected_count}</span>
                    </div>
                    <div className="mt-3 border-t border-zinc-100 pt-3 dark:border-zinc-800">
                        <div className="flex items-end justify-between">
                            <span className="font-mono text-[11px] uppercase tracking-wider text-zinc-500">Approved total</span>
                            <span className="font-display text-2xl font-bold" data-testid="pub-thankyou-total">
                                {rupees(result.total_approved_value_inr)}
                            </span>
                        </div>
                    </div>
                </div>
                <Button
                    asChild className="mt-4 w-full" variant="outline"
                    data-testid="pub-thankyou-pdf"
                >
                    <a href={PublicInspectionAPI.pdfUrl(token)} target="_blank" rel="noreferrer">
                        <Download className="mr-1.5 h-4 w-4" />
                        Download inspection report (PDF)
                    </a>
                </Button>
                <div className="mt-6 text-xs text-zinc-500">
                    The workshop will contact you shortly to confirm the work.
                </div>
                <div className="mt-8 font-mono text-[10px] uppercase tracking-widest text-zinc-400">
                    Powered by AutoBrix
                </div>
            </div>
        </div>
    );
}

export default function PublicInspectionPage() {
    const { token } = useParams();
    const [state, setState] = useState({ loading: true, data: null, error: null });
    const [decisions, setDecisions] = useState({});
    const [submitting, setSubmitting] = useState(false);
    const [result, setResult] = useState(null);
    const [signature, setSignature] = useState("");
    const [previewUrl, setPreviewUrl] = useState(null);
    const [tick, setTick] = useState(0);

    useEffect(() => {
        let alive = true;
        PublicInspectionAPI.get(token)
            .then((d) => { if (alive) setState({ loading: false, data: d, error: null }); })
            .catch((err) => {
                if (!alive) return;
                setState({ loading: false, data: null, error: err });
            });
        return () => { alive = false; };
    }, [token, tick]);

    const data = state.data;
    const allDecisionableFindings = useMemo(() => {
        if (!data) return [];
        const out = [];
        for (const sec of data.sections || []) {
            for (const it of sec.items || []) {
                if (it.severity === "yellow" || it.severity === "red") out.push(it);
            }
        }
        return out;
    }, [data]);

    const pendingDecisionable = allDecisionableFindings.filter(
        (f) => !(decisions[f.finding_id] || f.customer_decision),
    );

    const liveTally = useMemo(() => {
        let count = 0, totalInr = 0;
        for (const f of allDecisionableFindings) {
            const d = decisions[f.finding_id] || f.customer_decision;
            if (d === "approved") {
                count++;
                if (f.estimated_cost_inr) {
                    totalInr += Math.round(f.estimated_cost_inr * 1.18);
                }
            }
        }
        return { count, totalInr };
    }, [allDecisionableFindings, decisions]);

    function setDecision(findingId, value) {
        setDecisions((prev) => ({ ...prev, [findingId]: value }));
    }

    async function handleSubmit() {
        if (pendingDecisionable.length > 0) return;
        setSubmitting(true);
        try {
            const payload = {
                decisions: Object.entries(decisions).map(([finding_id, decision]) => ({
                    finding_id, decision,
                })),
            };
            if (signature.trim()) payload.customer_signature_name = signature.trim();
            const r = await PublicInspectionAPI.submitDecisions(token, payload);
            setResult(r);
        } catch (err) {
            if (err.status === 409) {
                // Server says already submitted — reload to fetch read-only view
                setTick((t) => t + 1);
            } else {
                setState((s) => ({ ...s, error: err }));
            }
        } finally {
            setSubmitting(false);
        }
    }

    // ============== Render guards ==============
    if (state.loading) {
        return (
            <div className="flex min-h-screen items-center justify-center bg-zinc-50 dark:bg-zinc-950">
                <Loader2 className="h-7 w-7 animate-spin text-zinc-400" data-testid="pub-loading" />
            </div>
        );
    }
    if (state.error) {
        if (state.error.status === 404) {
            return (
                <ErrorScreen
                    icon={AlertTriangle}
                    title="Link not found"
                    message="Please check the link or contact the workshop."
                />
            );
        }
        if (state.error.status === 410) {
            return (
                <ErrorScreen
                    icon={ShieldAlert}
                    title="Link no longer active"
                    message={state.error.message || "This inspection link has expired or been revoked. Please contact the workshop."}
                />
            );
        }
        return (
            <div className="flex min-h-screen items-center justify-center p-6 bg-zinc-50 dark:bg-zinc-950">
                <div className="max-w-sm text-center">
                    <AlertTriangle className="mx-auto h-10 w-10 text-zinc-400" />
                    <h1 className="mt-3 text-2xl font-bold">Could not load inspection</h1>
                    <p className="mt-2 text-zinc-600">{state.error.message}</p>
                    <Button className="mt-4" onClick={() => setTick((t) => t + 1)} data-testid="pub-retry">
                        Try again
                    </Button>
                </div>
            </div>
        );
    }
    if (result) {
        return <ThankYouScreen result={result} token={token} />;
    }
    // Already submitted (sub-status from server) → render read-only summary directly.
    if (data && data.submission_status === "submitted") {
        const approved = allDecisionableFindings.filter(
            (f) => f.customer_decision === "approved",
        );
        const rejected = allDecisionableFindings.filter(
            (f) => f.customer_decision === "rejected",
        );
        const totalInr = approved.reduce(
            (s, f) => s + (f.estimated_cost_inr ? Math.round(f.estimated_cost_inr * 1.18) : 0), 0,
        );
        return (
            <ThankYouScreen
                token={token}
                result={{
                    message: `Submitted on ${new Date(data.customer_reviewed_at).toLocaleString()}.`,
                    approved_count: approved.length,
                    rejected_count: rejected.length,
                    total_approved_value_inr: totalInr,
                }}
            />
        );
    }

    // ============== Main view ==============
    const summary = data.summary || {};
    return (
        <div className="min-h-screen bg-zinc-50 pb-32 dark:bg-zinc-950" data-testid="pub-inspection-page">
            {/* Header */}
            <header className="bg-white shadow-sm dark:bg-zinc-900">
                <div className="mx-auto max-w-3xl px-4 py-5 sm:px-6">
                    <div className="flex flex-col gap-1 sm:flex-row sm:items-start sm:justify-between">
                        <div>
                            <h1 className="font-display text-2xl font-bold tracking-tight" data-testid="pub-workshop-name">
                                {data.workshop.name}
                            </h1>
                            <p className="mt-0.5 text-sm text-zinc-500">{data.workshop.branch_name}</p>
                            {data.workshop.branch_address ? (
                                <p className="mt-1 flex items-center gap-1 text-xs text-zinc-500">
                                    <MapPin className="h-3 w-3" />
                                    {data.workshop.branch_address}
                                </p>
                            ) : null}
                        </div>
                        {data.workshop.branch_phone ? (
                            <a
                                href={`tel:${data.workshop.branch_phone}`}
                                className="mt-1 inline-flex items-center gap-1.5 text-sm font-semibold text-brand-redDeep"
                                data-testid="pub-workshop-phone"
                            >
                                <Phone className="h-3.5 w-3.5" />
                                {data.workshop.branch_phone}
                            </a>
                        ) : null}
                    </div>
                </div>
            </header>

            <main className="mx-auto max-w-3xl space-y-4 px-4 py-4 sm:px-6 sm:py-6">
                {/* Vehicle + severity rollup */}
                <section className="rounded-xl border border-zinc-200 bg-white p-4 dark:border-zinc-800 dark:bg-zinc-900">
                    <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                        <div>
                            <div className="font-mono text-[10px] uppercase tracking-widest text-zinc-500">
                                Your vehicle
                            </div>
                            <div className="mt-1 text-lg font-bold" data-testid="pub-vehicle">
                                {[data.vehicle.make, data.vehicle.model].filter(Boolean).join(" ")}
                                {data.vehicle.registration ? ` · ${data.vehicle.registration}` : ""}
                            </div>
                            {data.vehicle.odometer ? (
                                <div className="mt-0.5 text-xs text-zinc-500">
                                    Odometer: {data.vehicle.odometer.toLocaleString()} km
                                </div>
                            ) : null}
                        </div>
                        <div className="flex items-center gap-2" data-testid="pub-summary">
                            {summary.green_count > 0 && (
                                <span className="rounded-full bg-emerald-500 px-2.5 py-0.5 text-xs font-bold text-white">
                                    ✓ {summary.green_count}
                                </span>
                            )}
                            {summary.yellow_count > 0 && (
                                <span className="rounded-full bg-amber-500 px-2.5 py-0.5 text-xs font-bold text-white">
                                    ! {summary.yellow_count}
                                </span>
                            )}
                            {summary.red_count > 0 && (
                                <span className="rounded-full bg-rose-600 px-2.5 py-0.5 text-xs font-bold text-white">
                                    ✕ {summary.red_count}
                                </span>
                            )}
                        </div>
                    </div>
                </section>

                {/* Intro */}
                <section>
                    <h2 className="font-display text-xl font-bold tracking-tight">What we found</h2>
                    <p className="mt-1 text-sm text-zinc-600 dark:text-zinc-300">
                        We've completed your inspection. Items flagged{" "}
                        <span className="font-semibold text-amber-700 dark:text-amber-400">Advise</span> or{" "}
                        <span className="font-semibold text-rose-700 dark:text-rose-400">Urgent</span> need your call —
                        approve what you'd like fixed today, reject what can wait.
                    </p>
                </section>

                {/* Sections */}
                <div className="space-y-5">
                    {(data.sections || []).map((section) => (
                        <section key={section.id} data-testid={`pub-section-${section.id}`}>
                            <h3 className="mb-2 font-display text-sm font-bold uppercase tracking-wider text-zinc-500">
                                {section.name}
                            </h3>
                            <div className="space-y-2">
                                {section.items.map((item) => (
                                    <FindingCard
                                        key={item.finding_id}
                                        item={item}
                                        decisions={decisions}
                                        setDecision={setDecision}
                                        onPhotoClick={(u) => setPreviewUrl(u)}
                                    />
                                ))}
                            </div>
                        </section>
                    ))}
                </div>
            </main>

            {/* Sticky bottom bar — submit + tally */}
            <div className="fixed inset-x-0 bottom-0 z-30 border-t border-zinc-200 bg-white/95 backdrop-blur dark:border-zinc-800 dark:bg-zinc-950/95">
                <div className="mx-auto max-w-3xl space-y-2 px-4 py-3 sm:px-6">
                    <div className="flex items-center justify-between gap-3">
                        <div>
                            <div className="font-mono text-[10px] uppercase tracking-widest text-zinc-500">
                                Your approved total
                            </div>
                            <div className="flex items-center gap-2" data-testid="pub-live-tally">
                                <span className="font-display text-xl font-bold">
                                    {rupees(liveTally.totalInr)}
                                </span>
                                <span className="text-xs text-zinc-500">
                                    · {liveTally.count} item{liveTally.count === 1 ? "" : "s"}
                                </span>
                            </div>
                        </div>
                        <Button
                            type="button"
                            size="lg"
                            disabled={pendingDecisionable.length > 0 || submitting}
                            onClick={handleSubmit}
                            data-testid="pub-submit-btn"
                            className="min-w-[120px]"
                        >
                            {submitting ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
                            {pendingDecisionable.length === 0 ? "Submit" : `${pendingDecisionable.length} left`}
                        </Button>
                    </div>
                    <Input
                        type="text"
                        placeholder="Your name (optional, for our records)"
                        value={signature}
                        onChange={(e) => setSignature(e.target.value)}
                        className="text-sm"
                        data-testid="pub-signature"
                    />
                </div>
            </div>

            {previewUrl ? (
                <div
                    className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 p-4"
                    onClick={() => setPreviewUrl(null)}
                >
                    <img src={previewUrl} alt="" className="max-h-full max-w-full rounded-lg object-contain" />
                </div>
            ) : null}
        </div>
    );
}
